<template>
  <b-modal no-fade v-model="dialog.show" size="xl" :ref="`modal-account-${uniqueId}`"
           :id="`modal-account-${uniqueId}`"
           :hide-footer="true" centered @show="closeAllPopovers" @close="onDialogClose"
           no-close-on-backdrop
  >
    <template #modal-title>
      <span>
        {{ $t('columns.account') }}  {{ account != null ? `- ${account.id}` : '' }}
      </span>
    </template>
    <div style="min-height: 600px">
      <div v-if="loading !== true && error == null">
        <div v-if="account != null" >
          <!-- Account Overview Section -->
          <b-button variant="primary" class="text-left" block @click="accountOpen = !accountOpen">
            <span>{{ accountOpen ? '▼' : '▶' }}</span>
            <span class="mb-0 d-inline-block">Account Overview</span>
          </b-button>
          <b-collapse v-model="accountOpen">
            <b-card-body>
              <b-row>
                <b-col md="6">
                  <b-table-simple small>
                    <b-tbody>
                      <b-tr>
                        <b-td class="font-weight-bold">{{ $t('columns.account') }}:</b-td>
                        <b-td>{{ account.id }}</b-td>
                      </b-tr>
                      <b-tr class="cursor-pointer" @click="openClient">
                        <b-td class="font-weight-bold">{{ $t('columns.name') }}:</b-td>
                        <b-td><code class="small">{{ account.name }}</code></b-td>
                      </b-tr>
                      <b-tr>
                        <b-td class="font-weight-bold">{{ $t('columns.currency') }}:</b-td>
                        <b-td><b-badge variant="info">{{ account.currency }}</b-badge></b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-col>
                <b-col md="6">
                  <b-table-simple small>
                    <b-tbody>
                      <b-tr>
                        <b-td class="font-weight-bold">{{ $t('columns.defaultLeverage') }}:</b-td>
                        <b-td>1:{{ account.defaultLeverage }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-td class="font-weight-bold">{{ $t('columns.type') }}:</b-td>
                        <b-td>
                          <b-badge :variant="marginLevel.demo ? 'warning' : 'success'">
                            {{ marginLevel.demo ? 'DEMO' : 'LIVE' }}
                          </b-badge>
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-td class="font-weight-bold">{{ $t('columns.totalOpenTrades') }}:</b-td>
                        <b-td><b-badge variant="secondary">{{ marginLevel.totalOpenTrades }}</b-badge></b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-col>
              </b-row>
            </b-card-body>
          </b-collapse>
          <!-- Balance & Margin Summary Section -->
          <b-button variant="primary" class="text-left" block @click="balanceOpen = !balanceOpen">
            <span>{{ balanceOpen ? '▼' : '▶' }}</span>
            <span class="mb-0 d-inline-block">Balance & Margin Summary</span>
          </b-button>
          <b-collapse v-model="balanceOpen">
              <b-card-body>
                <b-row>
                  <b-col md="6">
                    <h6 class="border-bottom pb-2 mb-3">Balance Information</h6>
                    <b-table-simple small>
                      <b-tbody>
                        <b-tr>
                          <b-td class="font-weight-bold">{{ $t('columns.balance') }}:</b-td>
                          <b-td>{{ marginLevel.balance }} {{$t(`currencies.${account.currency}.symbol`)}}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-td class="font-weight-bold">{{ $t('columns.credit') }}:</b-td>
                          <b-td>{{ marginLevel.credit }} {{$t(`currencies.${account.currency}.symbol`)}}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-td class="font-weight-bold">Equity:</b-td>
                          <b-td>{{ marginLevel.equity }} {{$t(`currencies.${account.currency}.symbol`)}}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-td class="font-weight-bold">Profit/Loss:</b-td>
                          <b-td>
                            <b-badge :variant="profitLossVariant">
                              {{ profitLoss > 0 ? '+' : '' }}{{ profitLoss |toFixed}} {{$t(`currencies.${account.currency}.symbol`)}}
                            </b-badge>
                          </b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </b-col>
                  <b-col md="6">
                    <h6 class="border-bottom pb-2 mb-3">Margin Information</h6>
                    <b-table-simple small>
                      <b-tbody>
                        <b-tr>
                          <b-td class="font-weight-bold">Margin Used:</b-td>
                          <b-td>{{ marginLevel.margin | toFixed(2) }} {{$t(`currencies.${account.currency}.symbol`)}}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-td class="font-weight-bold">Margin Free:</b-td>
                          <b-td>{{ marginLevel.marginFree | toFixed(2)}} {{$t(`currencies.${account.currency}.symbol`)}}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-td class="font-weight-bold">Margin Level:</b-td>
                          <b-td>{{ marginLevel.marginLevel | toFixed(2) }}%</b-td>
                        </b-tr>
                        <b-tr>
                          <b-td class="font-weight-bold">Margin Status:</b-td>
                          <b-td>
                            <b-badge :variant="marginLevelVariant">
                              {{ marginLevel.marginLevelType.replace('MARGIN_LEVEL_', '') }}
                            </b-badge>
                          </b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </b-col>
                </b-row>

                <b-row class="mt-3">
                  <b-col cols="12">
                    <h6 class="border-bottom pb-2 mb-3">Margin Calculation Details</h6>
                    <b-row>
                      <b-col md="6">
                        <h6 class="text-muted small">Hedged Margin calculation</h6>
                        <b-table-simple small bordered>
                          <b-tbody>
                            <b-tr>
                              <b-td class="font-weight-bold">Margin Without Hedge:</b-td>
                              <b-td>{{ marginLevel.marginCalculationTotal.marginWithoutHedge }}</b-td>
                            </b-tr>
                            <b-tr>
                              <b-td class="font-weight-bold">Margin With Hedge:</b-td>
                              <b-td>{{ marginLevel.marginCalculationTotal.marginWithHedge }}</b-td>
                            </b-tr>
                          </b-tbody>
                        </b-table-simple>
                      </b-col>
                      <b-col md="6">
                        <h6 class="text-muted small">Hedged Margin by Symbol</h6>
                        <b-table-simple small bordered>
                          <b-tbody>
                            <b-tr v-for="(margin, symbol) in marginLevel.marginCalculationTotal.mergeWithHedgePerSymbol" :key="symbol">
                              <b-td><code class="small">{{ symbol }}</code></b-td>
                              <b-td class="text-right">{{ margin }}</b-td>
                            </b-tr>
                          </b-tbody>
                        </b-table-simple>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-collapse>

          <!-- Open Positions Section -->
          <b-button variant="primary" class="text-left" block @click="positionsOpen = !positionsOpen">
            <span>{{ positionsOpen ? '▼' : '▶' }}</span>
            <span class="mb-0 d-inline-block">Open Positions ({{ marginLevel.totalOpenTrades }})</span>
          </b-button>
          <b-collapse v-model="positionsOpen">
            <b-card-body>
              <b-table
                  striped
                  hover
                  responsive
                  v-handy-scroll
                  :items="positionsArray"
                  :fields="positionFields"
              >
                <template #cell(order)="data">
                  <b-badge variant="secondary">{{ data.value }}</b-badge>
                </template>
                <template #cell(symbol)="data">
                  <code>{{ data.value }}</code>
                </template>
                <template #cell(command)="data">
                  <b-badge variant="primary">{{ data.value }}</b-badge>
                </template>
                <template #cell(calculationMode)="data">
                  <small class="text-muted">{{ data.value.replace('MARGIN_CALC_', '') }}</small>
                </template>
                <template #cell(marginCurrency)="data">
                  {{ data.value.toUpperCase() }}
                </template>
                <template #cell(margin)="data">
                  <strong>{{ data.value }}</strong>
                </template>
                <template #cell(profit)="data">
                  <b-badge :variant="data.value > 0 ? 'success' : 'danger'">
                    {{ data.value | toFixed(3)}}
                  </b-badge>
                </template>
              </b-table>
            </b-card-body>
          </b-collapse>
        </div>

      </div>
      <div class="text-center position-absolute d-block w-100" style="top:50%" v-else-if="loading !== true && error != null">
        <b-alert show variant="danger">
          <i class="fa-sharp fa-solid fa-triangle-exclamation"></i>
          {{ error }}
        </b-alert>
      </div>
      <div class="text-center position-absolute d-block w-100" style="top:50%" v-else>
        <b-spinner>{{ $t('operations.loading') }}</b-spinner>
      </div>
    </div>
  </b-modal>
</template>
<script>
import { mapActions } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPopover from '@/components/mixin/mixinPopover'

export default {
  name: 'AccountModal',
  mixins: [mixinBase, mixinNotifications, mixinPopover],
  data: () => ({
    uniqueId: 0,
    dialog: {
      show: false,
    },
    loading: true,
    error: null,
    refreshInterval: null,
    accountId: null,
    account: null,
    marginLevel: null,
    openTrades: null,
    //
    accountOpen: true,
    balanceOpen: true,
    positionsOpen: true,
    positionFields: [
      { key: 'order', label: 'Order ID' },
      { key: 'symbol', label: 'Symbol' },
      { key: 'command', label: 'Command' },
      { key: 'calculationMode', label: 'Calculation Mode' },
      { key: 'marginCurrency', label: 'Margin Currency' },
      { key: 'marginBase', label: 'Margin Base' },
      { key: 'margin', label: 'Margin' },
      { key: 'profit', label: 'Profit' }
    ]
  }),
  created() {
    this.uniqueId = this._uid
    this.refreshInterval = setInterval(this.refreshData, 15*1000) // Refresh every 15 seconds
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
  },
  computed: {
    profitLoss() {
      if(this.marginLevel == null) return 0;
      return this.marginLevel.equity - this.marginLevel.balance;
    },
    profitLossVariant() {
      if(this.marginLevel == null) return 'secondary';
      return this.marginLevel.equity > this.marginLevel.balance ? 'success' : 'danger';
    },
    marginLevelVariant() {
      if(this.marginLevel == null) return 'secondary';
      const type = this.marginLevel.marginLevelType;
      if (type === 'MARGIN_LEVEL_OK') return 'success';
      if (type === 'MARGIN_LEVEL_WARNING') return 'warning';
      return 'danger';
    },
    positionsArray() {
      if(this.marginLevel == null || this.openTrades == null) return [];
      let marginMap = this.marginLevel.marginCalculationTotal?.marginMap || {};
      return this.openTrades.map(trade=>marginMap[trade.order] ? {...trade, ...marginMap[trade.order]} : trade);
      //return Object.values(this.marginLevel.marginCalculationTotal.marginMap);
    }
  },
  methods: {
    ...mapActions('account', ['getAccountByLogin']),
    showModal(accountId) {
      this.accountId = accountId || null
      this.dialog.show = true
      this.refreshData()
    },
    refreshData() {
      if(this.dialog.show !== true || this.accountId == null){
        return
      }
      let $this = this;
      this.getAccountByLogin(this.accountId)
          .then(data => {
            let tradingAccount = data?.tradingData?.account || {};
            let account = data.account || {};
            this.account = {...account, ...tradingAccount}
            this.marginLevel = data?.tradingData?.marginLevel
            this.openTrades = data?.openTrades || []
          },err => {
        $this.error = $this.getErrorDescription(err);
        $this.loading = false;
      }).finally(()=>{
        this.loading = false;
      });
    },
    onDialogClose() {
      this.accountId = null
      this.account = null
      this.marginLevel = null
      this.loading = null
      this.error = null
    },
    openClient() {
      this.dialog.show = false;
      this.showClient({ id: this.account.clientId, });
    },
  }
}
</script>


<style scoped>

</style>